'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, FileText, Zap } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof FileText;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: "Let's get started with message templates!",
    icon: FileText,
    body: (
      <>
        <p className="text-base">
          Message templates let you save and reuse common messages across your agency.
        </p>
        <p className="text-[15px]">
          Whether it&rsquo;s a welcome message, a follow-up, or a renewal reminder &mdash;
          templates help you respond faster and stay consistent.
        </p>
      </>
    ),
  },
  {
    heading: "Let's try some pre-loaded templates",
    icon: Zap,
    body: (
      <>
        <p className="text-base">
          We&rsquo;ve pre-loaded some starter templates for you. In the next few steps,
          we&rsquo;ll walk you through selecting one and seeing it in action.
        </p>
        <p className="text-[15px]">
          Templates automatically fill in contact details like names so every
          message feels personal, without extra effort.
        </p>
      </>
    ),
  },
];

interface TemplatesWelcomeModalProps {
  onComplete: () => void;
}

export function TemplatesWelcomeModal({ onComplete }: TemplatesWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="Templates Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let's Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
